/**
 * The checklist — the single biggest improvement on "an empty profile page".
 *
 * The owner's shape was "like an empty profile page (fill in, status, send to review and so on)".
 * A blank form is the part of that shape worth improving: it tells somebody nothing about what
 * getting in actually takes. So the page leads with the list of what is wanted, marks which four
 * of them are the gate, and every row moves focus to the control that satisfies it.
 *
 * THE ROWS COME FROM THE SERVER, NOT FROM THIS FILE. `profile_completeness_item` is a real table
 * whose nine rows are a product decision an operator may change, and `CompletenessItem.fieldCode`
 * exists precisely so a client reads the code rather than baking the set in. A row this build has
 * never seen still renders — with the server's own label, and simply without a jump target.
 *
 * ★ FOR AN APPROVED AUTHOR THE SAME ROWS ARE A PROFILE, NOT A GATE (design 14, finding F3). The
 * cabinet walk of 2026-10-01 showed an author the import brought over "Four of these are needed
 * before you can send" with two rows marked "needed" - on a page from which nothing is sent any
 * more. So when `rowsGateSending` says there is no gate, the list keeps every row and every tick
 * but drops the "needed" badges and says what the rows are for now; each row shows its weight,
 * which is the honest reason any of them is worth doing once none of them blocks anything.
 *
 * ★ A CALM CARD BESIDE THE FORM, AS THE PROTOTYPE DRAWS ITS LIST (the owner's live look at real
 * cabinets, 2026-10-01 / design 15): a title with an icon, a quiet line, and each row a tick box -
 * empty and outlined, then gold with a dark tick - in place of the old "✓" and "○" characters.
 * The box is decoration (`aria-hidden`); each row's button says "done" or "not done yet" in words.
 */
import { Badge, UnstyledButton } from '@mantine/core';
import { Check, ListChecks } from '@moonx/ui';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { Application } from '../contract';
import { checklistRows, rowsGateSending } from '../domain/completeness';
import classes from './profile.module.css';

export interface ChecklistProps {
  readonly application: Application;
  /** Moves focus to the control that satisfies a row. */
  readonly onJump: (anchorId: string) => void;
  /**
   * Rows the server named in a 422 as the reason a send was refused. Rendered louder than the
   * rest, because after a refusal the question is only ever "which one".
   */
  readonly highlightUnsatisfied?: boolean;
}

export function Checklist({
  application,
  onJump,
  highlightUnsatisfied = false,
}: ChecklistProps): ReactNode {
  const { t } = useTranslation('application');
  const rows = checklistRows(application);
  const gates = rowsGateSending(application);

  // The array's own order IS the contract — `CompletenessItem` carries no `sortOrder`, and the
  // field is documented as "the completeness checklist, in the order the cabinet renders it". So
  // it is rendered as sent and never re-sorted.
  if (rows.length === 0) {
    return (
      <section className={classes.sideCard} aria-labelledby="apply-checklist-title">
        <p className={classes.sideTitle} id="apply-checklist-title">
          <ListChecks size={16} aria-hidden="true" />
          {t('checklist.title.applying')}
        </p>
        <p className={classes.quiet} style={{ margin: 0 }}>
          {t('checklist.notLoaded')}
        </p>
      </section>
    );
  }

  return (
    <section className={classes.sideCard} aria-labelledby="apply-checklist-title">
      <p className={classes.sideTitle} id="apply-checklist-title">
        <ListChecks size={16} aria-hidden="true" />
        {gates ? t('checklist.title.applying') : t('checklist.title.author')}
      </p>
      <p className={classes.quiet} style={{ margin: 0 }}>
        {gates ? t('checklist.lede.applying') : t('checklist.lede.author')}
      </p>

      <ul className={classes.checkRows}>
        {rows.map(({ item, anchorId, hint }) => {
          // "Required" is a gate's word: with no gate (an approved author) no row is required.
          const needed = gates && item.isRequiredToSubmit;
          const wanted = needed && !item.isSatisfied;
          const box = `${classes.box} ${
            item.isSatisfied
              ? classes.boxDone
              : wanted && highlightUnsatisfied
                ? classes.boxWanted
                : ''
          }`;
          const body = (
            <>
              <span className={box} aria-hidden="true">
                {item.isSatisfied ? <Check size={12} strokeWidth={3.4} /> : null}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    lineHeight: 1.5,
                    fontWeight: needed ? 700 : 500,
                  }}
                >
                  {item.label}
                  {needed ? (
                    <Badge size="xs" variant="default">
                      {t('checklist.required')}
                    </Badge>
                  ) : null}
                </span>
                {hint !== undefined ? (
                  <span className={classes.quiet} style={{ display: 'block' }}>
                    {t(hint)}
                  </span>
                ) : null}
              </span>
              {/* The weight is the honest reason an optional row is worth doing at all. */}
              {!needed ? (
                <span className={classes.quiet} style={{ flex: 'none' }}>
                  +{item.weight}%
                </span>
              ) : null}
            </>
          );

          return (
            <li key={item.fieldCode}>
              {anchorId === undefined ? (
                <div className={classes.checkRow}>{body}</div>
              ) : (
                <UnstyledButton
                  className={classes.checkRow}
                  onClick={() => {
                    onJump(anchorId);
                  }}
                  aria-label={
                    item.isSatisfied
                      ? t('checklist.jump.done', { label: item.label })
                      : t('checklist.jump.notDone', { label: item.label })
                  }
                >
                  {body}
                </UnstyledButton>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
